import { useState } from 'react';
import { TrendingUp, ChevronRight } from 'lucide-react';
import { useTopRevenueLocations } from '@/hooks/useTopRevenueLocations';
import type { TopRevenueRange } from '@evi/shared';
import { formatCurrency } from './_lib';
import styles from '../DashboardPage.module.css';

/**
 * Top Revenue Locations widget. Top 10 revenue-grossing locations
 * over a 7d (default) or 30d window. Range toggle is local
 * useState — persistence across full reloads is a future
 * improvement if it matters.
 *
 * Click-through routes to /locations/:id via the existing
 * pushState wrapper. Falls back to a plain <a> if onNavigate
 * isn't wired (defensive; shouldn't happen in normal app shell).
 */
export default function TopRevenueLocationsWidget({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const [range, setRange] = useState<TopRevenueRange>('7d');
  const q = useTopRevenueLocations(range);
  const data = q.data;

  return (
    <div className={styles.chartBody}>
      <div className={styles.topRevHeader}>
        <div className={styles.chartIcon}><TrendingUp size={14} /></div>
        <p className={styles.chartTitle}>Top revenue locations</p>
        <div className={styles.topRevToggle} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={range === '7d'}
            className={`${styles.topRevToggleBtn} ${range === '7d' ? styles.topRevToggleBtnActive : ''}`}
            onClick={() => setRange('7d')}
          >
            7d
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={range === '30d'}
            className={`${styles.topRevToggleBtn} ${range === '30d' ? styles.topRevToggleBtnActive : ''}`}
            onClick={() => setRange('30d')}
          >
            30d
          </button>
        </div>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><TrendingUp size={18} /></span>
          <p>
            {data.is_scoped
              ? `No revenue at your assigned locations in the last ${range === '30d' ? '30 days' : '7 days'}.`
              : `No revenue in the last ${range === '30d' ? '30 days' : '7 days'}.`}
          </p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className={styles.topRevList}>
          {data.rows.map((row, idx) => (
            <a
              key={row.location_id}
              href={`/locations/${row.location_id}`}
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate(`/locations/${row.location_id}`);
                }
              }}
              className={styles.topRevRow}
            >
              <span className={styles.topRevRank}>{idx + 1}</span>
              <div className={styles.topRevBody}>
                <span className={styles.topRevName}>{row.location_name}</span>
                <span className={styles.topRevSessionCount}>
                  {row.session_count} session{row.session_count === 1 ? '' : 's'}
                </span>
              </div>
              <span className={styles.topRevAmount}>
                {formatCurrency(row.revenue_cents / 100)}
              </span>
              <ChevronRight size={14} className={styles.topRevChevron} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
